"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { useSession } from 'next-auth/react';
import { Image as ImageIcon, Film, FileText } from 'lucide-react';
import FeedCard, { type FeedItem } from "@/components/feed/FeedCard";
import ImageGalleryCard, { type ImageGalleryCardProps } from "@/components/ImageGalleryCard";
import PostCard, { type PostCardProps } from "@/components/PostCard";
import PostCardSkeleton from "@/components/skeletons/PostCard";
import VideoGalleryCard, { type VideoGalleryCardProps } from "@/components/VideoGalleryCard";
import InlineBanner from "@/components/ui/inline-banner";
import { EmptyIllustration, ErrorIllustration } from "@/components/ui/illustrations";
import ImageLightbox from "@/components/media/ImageLightbox";
import ProfileCTAs from "@/components/profile/ProfileCTAs";
import { toAbsoluteMediaUrl } from "@/lib/media";
import BlurGate from "@/components/common/BlurGate";
import { getApiToken as getBearer } from "@/lib/token";
import { apiFetch } from "@/lib/api";
import { apiRoutes } from "@/lib/apiRoutes";
import { scrubContactInfo } from "@/lib/scrubContact";

type FeedResp = { items?: FeedItem[]; nextCursor?: string | null } | FeedItem[];
function isArray<T>(x: any): x is T[] { return Array.isArray(x); }

function isChatMediaPath(s?: string | null): boolean {
  if (!s) return false;
  try {
    const raw = String(s);
    const p = /^https?:\/\//i.test(raw) ? new URL(raw).pathname : raw;
    const lower = p.toLowerCase();
    return (
      lower.includes('/chat/') ||
      lower.includes('/chat-') ||
      lower.includes('chat-library') ||
      lower.includes('chatlibrary') ||
      lower.includes('/library/chat') ||
      /(^|\/)chat(images|_images)?(\/|$)/.test(lower)
    );
  } catch { return false; }
}

export default function FeedList({ personId, initialItems, initialCursor, take = 12, layout = 'grid', showControls = false, controlsMode = 'full', ownerUserId = null, ownerPersonId = null, ownerUnsafe = false, ownerAvatar: ownerAvatarProp = null, ownerName: ownerNameProp = null, ownerSex: ownerSexProp = null, excludeTypes, headerLeft, hideItemAuthor = false, isOwner = false }: {
  personId: string | number;
  initialItems?: FeedItem[];
  initialCursor?: string | null;
  take?: number;
  layout?: 'grid' | 'list';
  showControls?: boolean;
  controlsMode?: 'full' | 'orderOnly' | 'typeOrder' | 'none';
  ownerUserId?: string | null;
  ownerPersonId?: string | number | null;
  /** When true, blur all media for guests regardless of per-item flags */
  ownerUnsafe?: boolean;
  /** Owner avatar/name/sex from the SSR-authenticated profile snapshot.
   *  When provided, FeedList will not refetch them — required to avoid an
   *  unauthenticated client fetch that returns the blurred avatar. */
  ownerAvatar?: string | null;
  ownerName?: string | null;
  ownerSex?: string | null;
  /** Types to hide from the feed entirely (e.g. ['image'] to hide individual images) */
  excludeTypes?: string[];
  /** Optional content rendered to the left of the type/sort controls in `typeOrder` mode. */
  headerLeft?: React.ReactNode;
  /** Profile timelines: hide the per-card author chip (every card belongs to the page owner). */
  hideItemAuthor?: boolean;
  /** Viewer owns this profile. Switches the empty state from "nothing here"
   *  to a prompt the owner can act on. */
  isOwner?: boolean;
}) {
  const { status } = useSession();
  const authed = status === 'authenticated';
  const router = useRouter();
  const hasInitial = Array.isArray(initialItems) && initialItems.length > 0;
  const [items, setItems] = useState<FeedItem[]>(() => (hasInitial ? initialItems!.map(normalize) : []));
  const [cursor, setCursor] = useState<string | null>(hasInitial && typeof initialCursor === 'string' ? initialCursor : "");
  const [loading, setLoading] = useState(!hasInitial);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [lightbox, setLightbox] = useState<{ src: string; alt?: string; caption?: string } | null>(null);
  const [filter, setFilter] = useState<'all' | 'video' | 'image' | 'gallery' | 'post'>('all');
  const [view, setView] = useState<'grid' | 'list'>(layout);
  const [order, setOrder] = useState<'desc' | 'asc'>('desc');
  const [ownerAvatar, setOwnerAvatar] = useState<string | null>(ownerAvatarProp);
  const [ownerName, setOwnerName] = useState<string | null>(ownerNameProp);
  const [ownerSex, setOwnerSex] = useState<string | null>(ownerSexProp);
  const ownerInjectedRef = useRef(false);
  const [commentCounts, setCommentCounts] = useState<Record<string, number>>({});

  function normalize(item: any): FeedItem & { createdAt?: string | null } {
    // Map various API shapes into our discriminated union
    const t = (item?.type || item?.kind || '').toString().toLowerCase();
    if (t === 'video') {
      return {
        id: item.id ?? item.videoId ?? item.VideoId,
        type: 'video',
        title: item.title ?? item.name ?? 'Video',
        thumbnailUrl: item.thumbnailUrl ?? item.thumbUrl ?? item.preview ?? item.ThumbnailUrl ?? item.thumbnailKey ?? item.ThumbnailKey ?? null,
        durationSeconds: item.durationSeconds ?? item.duration ?? null,
        viewCount: item.viewCount ?? item.views ?? null,
        // Both are returned by /api/video/v2 and were being dropped here. FeedCard
        // gates its like button on `typeof liked !== 'undefined'`, so losing
        // isLikedByMe left the button permanently disabled on every video row.
        // Deliberately NOT coerced to false: an absent value must stay undefined
        // so an unknown state keeps the button disabled rather than lying.
        likeCount: item.likeCount ?? item.LikeCount ?? null,
        isLikedByMe: item.isLikedByMe ?? item.IsLikedByMe,
        isUnsafeContent: Boolean(ownerUnsafe) || !!(item.isUnsafeContent ?? item.nsfw ?? item.sensitive),
        createdAt: item.createdAt ?? item.CreatedAt ?? item.datePublished ?? item.DatePublished ?? null
      } as any;
    }
    if (t === 'image' || t === 'photo') {
      const first = Array.isArray(item?.images) && item.images.length
        ? item.images[0]
        : (Array.isArray(item?.Images) && item.Images.length ? item.Images[0] : null);
      return {
        id: item.id ?? item.imageId ?? item.ImageId,
        type: 'image',
        title: item.title ?? null,
        src: item.src ?? item.url ?? item.path ?? first ?? null,
        description: item.description ?? item.caption ?? null,
        isUnsafeContent: Boolean(ownerUnsafe) || !!(item.isUnsafeContent ?? item.nsfw ?? item.sensitive),
        createdAt: item.createdAt ?? item.Created ?? item.created ?? item.timestamp ?? item.Timestamp ?? item.DatePublished ?? item.UploadDate ?? null
      } as any;
    }
    if (t === 'post' || t === 'text') {
      return {
        id: item.id ?? item.postId ?? item.PostId,
        type: 'post',
        title: item.title ?? item.Title ?? 'Post',
        snippet: item.snippet ?? item.summary ?? item.text ?? item.Text ?? null,
        thumbnailUrl: item.thumbnailUrl ?? item.thumb ?? null,
        isUnsafeContent: !!(item.isUnsafeContent ?? item.nsfw ?? item.sensitive),
        createdAt: item.createdAt ?? item.DatePosted ?? item.datePosted ?? null,
        // Preserve fields PostCard needs for ribbon, href, and city display
        categorySlug: item.categorySlug ?? item.CategorySlug ?? item.categoryName ?? item.CategoryName ?? null,
        locationSlug: item.locationSlug ?? item.LocationSlug ?? item.location ?? item.Location ?? null,
        cityName: item.cityName ?? item.CityName ?? item.city ?? item.City ?? null,
      } as any;
    }
    // Fallback passthrough
    return item as FeedItem;
  }

  const fetchPage = useCallback(async (cur: string | null) => {
    const qs = new URLSearchParams();
    if (cur) qs.set('cursor', cur);
    qs.set('take', String(take));
    const token = await getBearer();
    const authHdrs: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
    // Preferred: unified timeline route (static)
    let res = await fetch(`/api/profile/timeline?personId=${encodeURIComponent(String(personId))}&${qs.toString()}`, { cache: 'no-store', headers: authHdrs });
    if (res.status === 404) {
      // Dev fallback: older route (proxy + aggregation)
      const q2 = new URLSearchParams();
      q2.set('personId', String(personId));
      if (cur) q2.set('cursor', cur);
      q2.set('take', String(take));
      res = await fetch(`/api/feed?${q2.toString()}`, { cache: 'no-store', headers: authHdrs });
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = (await res.json()) as FeedResp;
    const raw = isArray<any>(json) ? json : (isArray<any>((json as any)?.items) ? (json as any).items : []);
    const arr = raw.map(normalize);
    const next = (json as any)?.nextCursor ?? null;
    return { arr, next } as { arr: FeedItem[]; next: string | null };
  }, [personId, take]);

  // If viewer is the owner, inject their private videos to the top (v2 mine=true)
  useEffect(() => {
    let cancelled = false;
    async function injectOwnerVideos() {
      try {
        if (ownerInjectedRef.current) return;
        // Fetch session lazily to check identity
        const res = await fetch('/api/auth/session', { cache: 'no-store', credentials: 'include' });
        if (!res.ok) return;
        const j = await res.json().catch(() => ({}));
        const myId: string | undefined = j?.user?.id;
        // If not authenticated, bail early to avoid unauthorized /api/profile requests
        if (!myId) return;
        let isOwner = false;
        if (myId && ownerUserId && myId === ownerUserId) {
          isOwner = true;
        }
        if (!isOwner) return;
        // Attach Authorization so proxy forwards to API with bearer token
        const token = await getBearer();
        const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
        let vres = await apiFetch(`/api/video/v2?mine=true&take=${encodeURIComponent(String(Math.max(6, take)))}`, { method: 'GET', cache: 'no-store' });
        // Fallback to legacy endpoint filtered by person if v2/mine isn't available
        if (vres.status === 401 || vres.status === 403 || vres.status === 404) {
          const legacy = await apiFetch(`/api/profile`, { method: 'GET', cache: 'no-store' });
          let personId: number | null = null;
          if (legacy.ok) {
            try { const pj = await legacy.json(); personId = Number(pj?.profile?.id ?? pj?.id ?? null) || null; } catch {}
          }
          if (personId) {
            vres = await apiFetch(`/api/video?personId=${encodeURIComponent(String(personId))}&limit=${encodeURIComponent(String(Math.max(6, take)))}`, { method: 'GET', cache: 'no-store' });
          }
        }
        if (!vres.ok) return;
        const raw = await vres.json().catch(() => null);
        const arr = Array.isArray(raw?.items) ? raw.items : (Array.isArray(raw) ? raw : []);
        if (!arr || arr.length === 0) return;
        // Ensure items are treated as videos (v2 might not include a 'type' field)
        const tagged = arr.map((v: any) => ({ ...(v || {}), type: (v?.type ?? 'video') }));
        const vids = tagged.map(normalize).filter((x: any) => (x && String(x.type).toLowerCase() === 'video')) as FeedItem[];
        if (!vids.length) return;
        if (cancelled) return;
        ownerInjectedRef.current = true;
        setItems(prev => {
          const seen = new Set<string>();
          const all = [...vids, ...prev];
          const dedup = all.filter((it) => {
            const key = `${String((it as any).type).toLowerCase()}:${String(it.id)}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          return dedup;
        });
      } catch { /* ignore */ }
    }
    injectOwnerVideos();
    return () => { cancelled = true; };
  }, [ownerUserId, ownerPersonId, take]);

  // Batch-fetch comment counts after items change. One round-trip per type.
  useEffect(() => {
    if (!items.length) return;
    let cancelled = false;
    const byType: Record<string, number[]> = {};
    for (const it of items) {
      const t = String((it as any)?.type || '').toLowerCase();
      const id = Number((it as any)?.id);
      if (!Number.isFinite(id) || id <= 0) continue;
      if (t !== 'post' && t !== 'gallery' && t !== 'video') continue;
      if (commentCounts[`${t}:${id}`] !== undefined) continue;
      (byType[t] ||= []).push(id);
    }
    const types = Object.keys(byType);
    if (!types.length) return;
    (async () => {
      const next: Record<string, number> = {};
      await Promise.all(types.map(async (t) => {
        try {
          const ids = Array.from(new Set(byType[t])).slice(0, 200).join(',');
          const res = await apiFetch(`/api/comments/counts?type=${encodeURIComponent(t)}&ids=${encodeURIComponent(ids)}`, { method: 'GET', cache: 'no-store' });
          if (!res.ok) return;
          const j: any = await res.json().catch(() => null);
          const counts = j?.counts || {};
          for (const k of Object.keys(counts)) {
            next[`${t}:${k}`] = Number(counts[k]) || 0;
          }
        } catch { /* ignore — count is decorative */ }
      }));
      if (!cancelled && Object.keys(next).length) {
        setCommentCounts(prev => ({ ...prev, ...next }));
      }
    })();
    return () => { cancelled = true; };
  }, [items, commentCounts]);

  // Fetch owner avatar/name only when not provided by caller. When the parent
  // already has them from an SSR-authenticated snapshot, refetching here would
  // be unauthenticated cross-origin (no cookie, no bearer) and the API would
  // return the BLURRED avatar — which then renders as the post-card thumbnail
  // for logged-in viewers. See vault/sites/juicystarz.md content-gating spec.
  useEffect(() => {
    if (ownerAvatarProp || ownerNameProp || ownerSexProp) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(apiRoutes.personById(String(personId)), { method: 'GET', cache: 'no-store' });
        if (!res.ok) return;
        const j = await res.json().catch(() => ({}));
        if (cancelled) return;
        const name = j?.displayName || j?.stageName || j?.firstName || null;
        const avatar = j?.avatarUrl || j?.profileImageLocation || null;
        setOwnerName(name);
        setOwnerAvatar(avatar ? String(avatar) : null);
        const sx = j?.gender ?? j?.sex ?? j?.Sex ?? null;
        setOwnerSex(sx ? String(sx) : null);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [personId, ownerAvatarProp, ownerNameProp, ownerSexProp]);

  // Initial
  useEffect(() => {
    if (hasInitial) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { arr, next } = await fetchPage("");
        if (cancelled) return;
        const seen = new Set<string>();
        const deduped = arr.filter((it: any) => {
          const key = `${String((it as any).type).toLowerCase()}:${String(it.id)}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setItems(deduped);
        setCursor(next);
        setHasMore(Boolean(arr.length && (next || arr.length >= take)));
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load feed');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [fetchPage, hasInitial, take]);

  // Infinite scroll
  useEffect(() => {
    if (!hasMore) return;
    const el = sentinelRef.current; if (!el) return;
    const io = new IntersectionObserver(async (ents) => {
      const first = ents[0]; if (!first?.isIntersecting) return;
      if (loadingMore) return;
      setLoadingMore(true);
      try {
        const { arr, next } = await fetchPage(cursor);
        setItems((prev) => {
          const seen = new Set<string>(prev.map((it: any) => `${String((it as any).type).toLowerCase()}:${String(it.id)}`));
          const fresh = arr.filter((it: any) => {
            const key = `${String((it as any).type).toLowerCase()}:${String(it.id)}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          return [...prev, ...fresh];
        });
        setCursor(next);
        setHasMore(Boolean(arr.length && (next || arr.length >= take)));
      } catch (e: any) {
        setError(e?.message || 'Failed to load more');
      } finally { setLoadingMore(false); }
    }, { rootMargin: '400px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [cursor, hasMore, loadingMore, fetchPage, take]);

  const filtered = useMemo(() => {
    const base = items.filter((it) => {
      const t = String((it as any)?.type || '').toLowerCase();
      if (t === 'image' || t === 'photo') {
        const any: any = it as any;
        const src = any?.src || any?.url || any?.path || (Array.isArray(any?.images) && any.images[0]) || '';
        if (isChatMediaPath(src)) return false;
      }
      return true;
    });
    const excluded = excludeTypes?.map(s => s.toLowerCase()) ?? [];
    const visible = excluded.length ? base.filter(it => !excluded.includes(String((it as any)?.type || '').toLowerCase())) : base;
    if (filter === 'all') return visible;
    return visible.filter((it) => {
      const t = String((it as any)?.type || '').toLowerCase();
      return t === filter;
    });
  }, [items, filter, excludeTypes]);

  const ordered = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a: any, b: any) => {
      const ta = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return order === 'desc' ? (tb - ta) : (ta - tb);
    });
    return arr;
  }, [filtered, order]);

  const fullControls = showControls && controlsMode === 'full';
  const orderOnly = showControls && controlsMode === 'orderOnly';
  const typeOrder = showControls && controlsMode === 'typeOrder';
  const TypeChips = (
    // max-w-full + wrap: at 337px+ this pill has almost no slack on a 390px
    // phone, and iOS renders text wider than Chrome — without these it pushes
    // past the screen edge and the whole page scrolls horizontally.
    <div className="inline-flex max-w-full flex-wrap items-center gap-1 rounded-2xl border border-border bg-card text-card-foreground p-1 shadow-sm">
      {(([
        { key: 'all', label: 'All', icon: null },
        { key: 'post', label: 'Posts', icon: 'post' },
        { key: 'image', label: 'Photos', icon: 'image' },
        { key: 'video', label: 'Videos', icon: 'video' },
        { key: 'gallery', label: 'Galleries', icon: 'image' },
      ] as const))
        .filter(t => t.key === 'all' || !(excludeTypes ?? []).map(s => s.toLowerCase()).includes(t.key))
        .map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => setFilter(t.key)}
          className={`px-2.5 sm:px-3 py-1.5 text-sm font-medium rounded-xl inline-flex items-center gap-1.5 sm:gap-2 transition-colors duration-150 ${filter === t.key ? 'bg-primary text-primary-foreground shadow-sm' : 'hover:bg-accent text-muted-foreground hover:text-foreground'}`}
          aria-pressed={filter === t.key}
          aria-current={filter === t.key ? 'true' : undefined}
        >
          {t.icon === 'image' ? (
            <ImageIcon className="h-4 w-4" aria-hidden="true" />
          ) : t.icon === 'video' ? (
            <Film className="h-4 w-4" aria-hidden="true" />
          ) : t.icon === 'post' ? (
            <FileText className="h-4 w-4" aria-hidden="true" />
          ) : null}
          <span>{t.label}</span>
        </button>
      ))}
    </div>
  );
  const ViewControls = fullControls ? (
    <div className="flex items-center justify-between flex-wrap gap-2">
      {TypeChips}
      <div className="inline-flex items-center gap-1 border rounded-md p-1 bg-background">
        {(['list', 'grid'] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setView(v)}
            className={`px-2.5 py-1 text-xs rounded inline-flex items-center gap-1.5 ${view === v ? 'bg-primary text-primary-foreground ring-2 ring-primary shadow-sm' : 'hover:bg-muted text-muted-foreground'}`}
            aria-pressed={view === v}
            aria-current={view === v ? 'true' : undefined}
            aria-label={v === 'list' ? 'List view' : 'Grid view'}
            title={v === 'list' ? 'List view' : 'Grid view'}
          >
            {v === 'list' ? (
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
            ) : (
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="4" width="7" height="7"/><rect x="13" y="4" width="7" height="7"/><rect x="4" y="13" width="7" height="7"/><rect x="13" y="13" width="7" height="7"/></svg>
            )}
            <span className="hidden sm:inline">{v === 'list' ? 'List' : 'Grid'}</span>
          </button>
        ))}
      </div>
    </div>
  ) : orderOnly ? (
    <div className="flex items-center justify-end">
      <div className="inline-flex items-center gap-1 border rounded-full p-1 bg-background shadow-sm">
        {(['desc','asc'] as const).map(v => (
          <button
            key={v}
            type="button"
            onClick={() => setOrder(v)}
            className={`px-3 py-1.5 text-xs rounded-full inline-flex items-center gap-1.5 ${order === v ? 'bg-primary text-primary-foreground ring-2 ring-primary shadow-sm' : 'hover:bg-muted text-muted-foreground'}`}
            aria-pressed={order === v}
            aria-current={order === v ? 'true' : undefined}
            aria-label={v === 'desc' ? 'Newest first' : 'Oldest first'}
            title={v === 'desc' ? 'Newest first' : 'Oldest first'}
          >
            {v === 'desc' ? 'Newest' : 'Oldest'}
          </button>
        ))}
      </div>
    </div>
  ) : typeOrder ? (
    <div className="flex items-center flex-wrap gap-2">
      {headerLeft}
      {TypeChips}
      <div className="ml-auto inline-flex items-center gap-1 rounded-2xl border border-border bg-card text-card-foreground p-1 shadow-sm">
        {(['desc','asc'] as const).map(v => (
          <button
            key={v}
            type="button"
            onClick={() => setOrder(v)}
            className={`px-3 py-1.5 text-sm font-medium rounded-xl inline-flex items-center gap-1.5 transition-colors duration-150 ${order === v ? 'bg-primary text-primary-foreground shadow-sm' : 'hover:bg-accent text-muted-foreground hover:text-foreground'}`}
            aria-pressed={order === v}
            aria-current={order === v ? 'true' : undefined}
            aria-label={v === 'desc' ? 'Newest first' : 'Oldest first'}
            title={v === 'desc' ? 'Newest first' : 'Oldest first'}
          >
            {v === 'desc' ? 'Newest' : 'Oldest'}
          </button>
        ))}
      </div>
    </div>
  ) : null;
  const GatingNote = (!authed && (filter === 'image' || filter === 'video')) ? (
    <InlineBanner variant="info" className="mb-2">Login to view sensitive media.</InlineBanner>
  ) : null;

  return (
    <section className="space-y-4">
      <ImageLightbox src={lightbox?.src || undefined} alt={lightbox?.alt} caption={lightbox?.caption} open={!!lightbox} onClose={() => setLightbox(null)} />

      {loading ? (
        <div aria-busy="true" role="progressbar" aria-valuetext="Loading">
          <p className="sr-only" aria-live="polite">Loading feed…</p>
          {ViewControls}
          {GatingNote}
          {view === 'grid' ? (
            <div aria-hidden="true" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
              {Array.from({ length: Math.max(6, take) }).map((_, i) => (
                <div key={i} className="border rounded-lg overflow-hidden">
                  <div className="aspect-video skeleton" />
                  <div className="p-3 space-y-2">
                    <div className="h-4 w-3/4 skeleton rounded" />
                    <div className="h-3 w-1/3 skeleton rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div aria-hidden="true" className="space-y-3 mt-2">
              {Array.from({ length: Math.max(6, take) }).map((_, i) => (
                <PostCardSkeleton key={i} />
              ))}
            </div>
          )}
        </div>
      ) : error ? (
        <div className="text-sm text-muted-foreground">Failed to load feed.</div>
      ) : ordered.length === 0 ? (
        <>
          {ViewControls}
          {GatingNote}
          <div className="flex items-center justify-center min-h-[300px] py-12">
            <div className="text-center max-w-md mx-auto px-4">
              <div className="mx-auto w-16 h-16 rounded-full border-2 border-dashed border-muted-foreground/30 flex items-center justify-center mb-4">
                {filter === 'video' ? (
                  <Film className="h-8 w-8 text-muted-foreground/50" />
                ) : (
                  <ImageIcon className="h-8 w-8 text-muted-foreground/50" />
                )}
              </div>
              <h3 className="text-lg font-semibold mb-2">No {filter === 'all' ? 'content' : filter === 'image' ? 'photos' : filter === 'video' ? 'videos' : filter === 'gallery' ? 'galleries' : filter === 'post' ? 'posts' : 'content'} yet</h3>
              {/* Name who fills this gap. "Check back later" told a visitor
                  nothing about whether the profile was real-but-new or
                  abandoned, and told the owner nothing to do about it. */}
              {isOwner ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    Your profile shows up in search once it has work on it. Galleries and videos are how producers find you.
                  </p>
                  <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-sm">
                    <a
                      href="/profile/galleries/create"
                      className="inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
                    >
                      Upload a gallery <span aria-hidden>→</span>
                    </a>
                    <a className="underline hover:no-underline" href="/profile/videos/create">Upload a video</a>
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {ownerNameProp ? `${ownerNameProp} hasn` : 'This creator hasn'}&apos;t published anything yet. New profiles usually fill in over their first few weeks.
                </p>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          {ViewControls}
          {GatingNote}
          {view === 'grid' && fullControls ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {ordered.map((raw, idx) => {
                const it: any = raw;
                const type = String(it?.type || '').toLowerCase();
                if (type === 'gallery') {
                  const props: ImageGalleryCardProps = {
                    galleryId: Number(it.id ?? 0) || 0,
                    name: it.name ?? undefined,
                    description: it.description ?? undefined,
                    images: Array.isArray(it.images) ? it.images : [],
                    // Blur entire gallery for guests when owner or item is unsafe
                    unsafe: Boolean(ownerUnsafe || it.isUnsafeContent),
                    commentCount: commentCounts[`gallery:${Number(it.id ?? 0) || 0}`],
                  };
                  return (
                    <div key={`gal-${props.galleryId}-${idx}`} className="space-y-2">
                      <ImageGalleryCard {...props} />
                      {/* CTAs removed under items */}
                    </div>
                  )
                }
                if (type === 'post') {
                  const props: PostCardProps = {
                    postId: Number(it.id ?? 0) || 0,
                    title: (() => {
                      const t = String(it.title ?? '').trim();
                      const looksReal = t && t.toLowerCase() !== 'post';
                      if (looksReal) return t;
                      const s = String(it.snippet ?? '').trim();
                      return s || 'Untitled';
                    })(),
                    details: String(it.snippet ?? ''),
                    stageName: ownerName || 'Poster',
                    avatarUrl: String(it.thumbnailUrl ?? it.thumbUrl ?? ownerAvatar ?? ''),
                    age: 0,
                    cityName: String(it.cityName ?? it.city ?? ''),
                    // Raw slug, not uppercased: PostCard humanizes it.
                    categoryDescription: String(it.categorySlug ?? it.categoryDescription ?? '') || 'Post',
                    locationSlug: String(it.locationSlug ?? 'anywhere'),
                    categorySlug: String(it.categorySlug ?? 'general'),
                    showMeta: false,
                    variant: 'light',
                    owner: ownerAvatar ? { displayName: ownerName || undefined, avatarUrl: ownerAvatar || undefined } : undefined,
                    unsafe: Boolean(ownerUnsafe || it.isUnsafeContent),
                    commentCount: commentCounts[`post:${Number(it.id ?? 0) || 0}`],
                    createdAt: (it as any)?.createdAt ?? null,
                    sex: ownerSex || undefined,
                    showCategoryRibbon: true,
                    showAuthor: !hideItemAuthor,
                  };
                  return (
                    <div key={`post-${props.postId}-${idx}`} className="space-y-2">
                      <PostCard {...props} />
                      {/* CTAs removed under items */}
                    </div>
                  )
                }
                if (type === 'video') {
                  const props: VideoGalleryCardProps = {
                    id: Number(it.id ?? 0) || 0,
                    title: String(it.title ?? 'Video'),
                    thumbnailUrl: String(it.thumbnailUrl ?? it.thumbUrl ?? it.preview ?? ''),
                    viewCount: typeof it.viewCount === 'number' ? it.viewCount : null,
                    durationSeconds: typeof it.durationSeconds === 'number' ? it.durationSeconds : null,
                    unsafe: Boolean(ownerUnsafe || it.isUnsafeContent),
                  };
                  return (
                    <div key={`vid-${props.id}-${idx}`} className="space-y-2">
                      <VideoGalleryCard {...props} />
                      {/* CTAs removed under items */}
                    </div>
                  )
                }
                // single image as a mini gallery card with one image
                if (type === 'image') {
                  const img = it.src || (Array.isArray(it.images) && it.images.length ? it.images[0]?.location || it.images[0] : null);
                  const props: ImageGalleryCardProps = {
                    galleryId: Number(it.id ?? 0) || 0,
                    name: it.title ?? undefined,
                    description: it.description ?? undefined,
                    images: img ? [{ location: img }] : [],
                    unsafe: Boolean(ownerUnsafe || it.isUnsafeContent),
                  };
                  return (
                    <div key={`img-${props.galleryId}-${idx}`} className="space-y-2">
                      <ImageGalleryCard {...props} />
                      {/* CTAs removed under items */}
                    </div>
                  )
                }
                return <FeedCard key={`${(it as any).id ?? idx}-${idx}`} item={it as FeedItem} onOpenImage={(src, alt, caption) => setLightbox({ src, alt, caption })} />
              })}
            </div>
         ) : (
            <div className="space-y-3">
              {ordered.map((raw, idx) => {
                const it = raw as any;
                const type = String(it.type || '').toLowerCase();
                const id = (it.id ?? idx);
                const isVideo = type === 'video';
                const isImage = type === 'image';
                const isPost  = type === 'post';
                const isGallery = type === 'gallery';
                const created = it?.createdAt ? new Date(it.createdAt) : null;
                const icon = isVideo ? (<Film className="h-3.5 w-3.5" />) : isImage || isGallery ? (<ImageIcon className="h-3.5 w-3.5" />) : (<FileText className="h-3.5 w-3.5" />);
                const dateStr = created ? created.toLocaleDateString() : '';
                if (isGallery) {
                  const props: ImageGalleryCardProps = {
                    galleryId: Number(it.id ?? 0) || 0,
                    name: it.name ?? undefined,
                    description: it.description ?? undefined,
                    images: Array.isArray(it.images) ? it.images : [],
                    unsafe: Boolean(ownerUnsafe || it.isUnsafeContent),
                    commentCount: commentCounts[`gallery:${Number(it.id ?? 0) || 0}`],
                  };
                  return (
                    <ImageGalleryCard key={`gal-${props.galleryId}-${idx}`} {...props} />
                  );
                }
                if (isPost) {
                  const props: PostCardProps = {
                    postId: Number(it.id ?? 0) || 0,
                    title: (() => {
                      const t = String(it.title ?? '').trim();
                      const looksReal = t && t.toLowerCase() !== 'post';
                      if (looksReal) return t;
                      const s = String(it.snippet ?? '').trim();
                      return s || 'Untitled';
                    })(),
                    details: String(it.snippet ?? ''),
                    stageName: ownerName || 'Poster',
                    avatarUrl: String(it.thumbnailUrl ?? it.thumbUrl ?? ownerAvatar ?? ''),
                    age: 0,
                    sex: ownerSex ? String(ownerSex) : undefined,
                    cityName: String(it.cityName ?? it.city ?? ''),
                    // Raw slug, not uppercased: PostCard humanizes it.
                    categoryDescription: String(it.categorySlug ?? it.categoryDescription ?? '') || 'Post',
                    locationSlug: String(it.locationSlug ?? 'anywhere'),
                    categorySlug: String(it.categorySlug ?? 'general'),
                    showMeta: false,
                    variant: 'light',
                    owner: ownerAvatar ? { displayName: ownerName || undefined, avatarUrl: ownerAvatar || undefined } : undefined,
                    unsafe: Boolean(ownerUnsafe || it.isUnsafeContent),
                    commentCount: commentCounts[`post:${Number(it.id ?? 0) || 0}`],
                    createdAt: (it as any)?.createdAt ?? null,
                    showCategoryRibbon: true,
                    showAuthor: !hideItemAuthor,
                  };
                  return <PostCard key={`post-${props.postId}-${idx}`} {...props} />;
                }
                if (isVideo) {
                  const props: VideoGalleryCardProps = {
                    id: Number(it.id ?? 0) || 0,
                    title: String(it.title ?? 'Video'),
                    thumbnailUrl: String(it.thumbnailUrl ?? it.thumbUrl ?? it.preview ?? ''),
                    viewCount: typeof it.viewCount === 'number' ? it.viewCount : null,
                    durationSeconds: typeof it.durationSeconds === 'number' ? it.durationSeconds : null,
                    unsafe: Boolean(ownerUnsafe || it.isUnsafeContent),
                  };
                  return <VideoGalleryCard key={`vid-${props.id}-${idx}`} {...props} />;
                }
                const thumb = isVideo ? (it.thumbnailUrl || it.thumbUrl || it.preview || null)
                             : isImage ? (it.src || (Array.isArray(it.images) && it.images.length ? it.images[0] : null))
                             : (it.thumbnailUrl || null);
                const img = thumb ? toAbsoluteMediaUrl(String(thumb)) : '';
                const title = it.title || (isPost ? 'Post' : isVideo ? 'Video' : 'Image');
                const description = it.description || it.snippet || it.text || '';
                const openImage = () => {
                  if (!isImage || !img) return;
                  const gated = Boolean((it as any)?.isUnsafeContent || ownerUnsafe);
                  if (!authed && gated) {
                    try {
                      const ret = typeof window !== 'undefined' ? window.location.pathname : `/details/${encodeURIComponent(String(personId))}`;
                      router.push(`/login?returnTo=${encodeURIComponent(ret)}`);
                    } catch {}
                    return;
                  }
                  setLightbox({ src: img, alt: title, caption: description });
                };
                return (
                  <React.Fragment key={`${id}-${idx}`}>
                    <article className="rounded-lg border bg-card text-card-foreground overflow-hidden">
                      <div className="flex gap-3">
                        <div className="relative w-40 sm:w-48 md:w-56 aspect-video bg-muted flex-shrink-0">
                          {img ? (
                            <Image src={img} alt={title} fill className="object-cover" sizes="(min-width: 1024px) 20vw, 40vw" onClick={openImage as any} />
                          ) : (
                            <div className="absolute inset-0 skeleton" />
                          )}
                          <BlurGate unsafe={!!it.isUnsafeContent} />
                          {isVideo && typeof it.durationSeconds === 'number' && (
                            <span className="absolute bottom-1 right-1 rounded bg-black/70 text-white text-[11px] px-1.5 py-0.5">
                              {formatDuration(it.durationSeconds)}
                            </span>
                          )}
                      </div>
                      <div className="flex-1 py-2 pr-3 min-w-0">
                        <div className="text-sm font-medium truncate mb-1">{scrubContactInfo(title)}</div>
                        {description ? (
                          <div className="text-xs text-muted-foreground line-clamp-2">{scrubContactInfo(description)}</div>
                        ) : null}
                        <div className="mt-2 text-[11px] text-muted-foreground flex items-center gap-2">
                          <span className="uppercase tracking-wide">{type}</span>
                          {typeof it.viewCount === 'number' && <span>• {it.viewCount.toLocaleString()} views</span>}
                        </div>
                      </div>
                    </div>
                    </article>
                    {/* Removed inline CTAs under feed items on details page */}
                  </React.Fragment>
                );
              })}
            </div>
          )}
          <div ref={sentinelRef} className="h-10" />
          {loadingMore && (
            view === 'grid' ? (
              <div aria-hidden className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={`lm-g-${i}`} className="border rounded-lg overflow-hidden">
                    <div className="aspect-video skeleton" />
                    <div className="p-3 space-y-2">
                      <div className="h-4 w-3/4 skeleton rounded" />
                      <div className="h-3 w-1/3 skeleton rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div aria-hidden className="space-y-3 mt-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <article key={`lm-l-${i}`} className="rounded-lg border bg-card text-card-foreground overflow-hidden">
                    <div className="flex gap-3">
                      <div className="relative w-40 sm:w-48 md:w-56 aspect-video skeleton flex-shrink-0" />
                      <div className="flex-1 py-2 pr-3 min-w-0 space-y-2">
                        <div className="h-4 w-2/3 skeleton rounded" />
                        <div className="h-3 w-3/5 skeleton rounded" />
                        <div className="h-3 w-24 skeleton rounded" />
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )
          )}
        </>
      )}
    </section>
  );
}

function formatDuration(sec?: number | null): string {
  if (!sec || sec <= 0) return '';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
